<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { usePageStore } from '../../stores/page'
import { useImgSrcSet } from '../../composables/use-img-src-set'
import Icon from '../shared/Icon.vue'

import type { Brand } from '@xcel-next/core/enums'
import type { ComposedMenu, ComposedPage } from '@xcel-next/composer-schema'

type MenuItemType = ComposedMenu[number]
type MenuColumn = NonNullable<MenuItemType['columns']>[number]
type ExtendedMenu = NonNullable<NonNullable<ComposedPage['header']>['extendedMenus']>[number]
type ResolvedMenuColumn = MenuColumn & { isExtendedMenuColumn?: boolean, isAppendedExtendedMenuColumn?: boolean, extendedMenu?: ExtendedMenu }

const props = withDefaults(defineProps<{
  menuItem?: MenuItemType,
  level?: number,
  menuType?: 'header' | 'footer',
  menuContext?: 'burger' | 'desktop' | 'footer',
  brand?: Brand,
  openedMenuItem?: string,
  burgerOnly?: boolean
}>(), {
  menuItem: () => ({}),
  level: 0,
  menuType: 'header',
  menuContext: 'footer',
  burgerOnly: false
})

const emit = defineEmits<{
  (e: 'menu-item-hovered', value: string): void
}>()

const { pageData } = storeToRefs(usePageStore())
const { createImgSrc } = useImgSrcSet()

const rootEl = ref<HTMLElement | null>(null)
const showDropDownMenu = ref(false)
const showColumn = ref<number | null>(null)

const rel = computed(() => {
  if (props.menuItem.target === '_blank' && props.menuItem.url && !props.menuItem.url.startsWith('/')) {
    // external links with target blank should have rel="noopener" for security and lighthouse score
    return 'noopener'
  } else {
    return ''
  }
})

const hasChildren = computed(() => !!(props.menuItem.children && props.menuItem.children.length > 0))

const imageIconUrl = computed(() => props.menuItem.icon
  ? createImgSrc({
    image: props.menuItem.icon,
    brand: props.brand ?? '',
    type: 'webp',
    setName: 'partnerBrandLogo'
  }) ?? undefined
  : undefined)

const flyoutId = computed(() => `flyout-${props.menuContext}-${(props.menuItem.name ?? '').replace(/\s+/g, '-').toLowerCase()}`)

// to remove data logic from the template, we will resolve the columns here already,
// so that the template can just loop over the resolved columns without caring about
// the logic if there is an extended menu that should be rendered within the column
// or as a separate column.
const rawColumns = computed(() => props.menuItem.columns ?? [])

// lets check if there are any columns that reference an extended menu,
// if yes, we need to fetch the names of those extended menus to check later
// if the extended menu that should be rendered appended to the regular
// children of the menu item has a matching column or not.
const inlineExtendedMenuNames = computed(() => rawColumns.value
  .map(column => column.menuExtension)
  .filter(Boolean))

// fetch information about the extended menu that should be rendered appended to the regular children of the menu item.
// This is needed for the desktop view, because in the desktop view we want to render the extended menu as a separate column
// if there is no matching column for it already, otherwise we want to render it within the matching column.
const mainExtendedMenuName = computed(() => props.menuItem.menuExtension ?? null)

// check if there is a column that references the same extended menu as the one that should be rendered appended
// to the regular children of the menu item, if yes, we want to render the extended menu within that column
// in the desktop view, otherwise we want to render it as a separate column.
const hasInlineExtendedMenuMatch = computed(() =>
  !!(mainExtendedMenuName.value && inlineExtendedMenuNames.value.includes(mainExtendedMenuName.value)))

// check if we should render the extended menu that should be rendered appended to the regular children of the menu item,
// this is the case if there is no matching column for it or if we are not in the desktop view,
// because in the mobile view we always want to render it appended to the regular children of the menu item,
// even if there is a matching column for it.
const shouldRenderAppendedExtendedMenu = computed(() => {
  if (!mainExtendedMenuName.value) return false
  // Desktop: wenn A und B dieselbe Extension referenzieren, A ignorieren
  return !(props.menuContext === 'desktop' && hasInlineExtendedMenuMatch.value)
})

// fetch the extended menu that should be rendered appended to the regular children of the menu item, if there is one and if we should render it but only on desktop
const appendedExtendedMenu = computed(() => {
  if (!shouldRenderAppendedExtendedMenu.value || props.menuContext !== 'desktop') return null

  return pageData.value?.header?.extendedMenus?.find(
    ({ name }) => name === mainExtendedMenuName.value
  ) ?? null
})

// finally resolve the columns, if there is an extended menu that should be rendered appended to the regular children of the menu item
// and there is a matching column for it, we want to render the extended menu within that column,
// otherwise we want to render it as a separate column (only in desktop view).
const resolvedColumns = computed(() => {
  const columns: ResolvedMenuColumn[] = rawColumns.value.map(column => {
    if (!column.menuExtension) return column

    return {
      ...column,
      isExtendedMenuColumn: true,
      title: column.menuExtension
    }
  })

  if (shouldRenderAppendedExtendedMenu.value && appendedExtendedMenu.value?.menu?.length) {
    columns.push({
      title: appendedExtendedMenu.value.name,
      menuExtension: appendedExtendedMenu.value.name,
      isExtendedMenuColumn: true,
      isAppendedExtendedMenuColumn: true,
      extendedMenu: appendedExtendedMenu.value,
      children: appendedExtendedMenu.value.menu
    })
  }
  return columns
})

// check if there are any columns to be rendered, this is the case if there are any regular columns
// or if there is an extended menu that should be rendered as a separate column in the desktop view.
const hasColumns = computed(() => resolvedColumns.value.length > 0)

// check if there are any children to be rendered, this is the case if there are any regular children
// or if there is an extended menu that should be rendered appended to the regular children of
const hasFlyoutContent = computed(() => hasChildren.value || hasColumns.value || !!appendedExtendedMenu.value)

watch(() => props.openedMenuItem, (newVal) => {
  if (props.menuItem.name !== newVal) {
    showDropDownMenu.value = false
  }
})

const isElementReallyVisible = (el: HTMLElement) => {
  const rect = el.getBoundingClientRect()
  const centerX = rect.left + rect.width / 2
  const centerY = rect.top + rect.height / 2
  const topElement = document.elementFromPoint(centerX, centerY)
  return el === topElement || !!(topElement && el.contains(topElement))
}

const handleColumnFlyoutPositioning = () => {
  // lets check if the column flyout is inside the viewport, otherwise we will correct the left margin to move it into the viewport.
  nextTick(() => {
    const flyout = rootEl.value?.querySelector<HTMLElement>('.bx-js-is-dropdown')
    if (!flyout) return

    const flyoutRect = flyout.getBoundingClientRect()

    if (flyoutRect.x < 0) {
      flyout.style.marginLeft = `${-flyoutRect.x + 20}px`
    }
  })
}

const handleMouseOver = (event: MouseEvent) => {
  if (props.menuContext !== 'desktop' ||
      (!hasChildren.value && !hasColumns.value) ||
      !isElementReallyVisible(event.target as HTMLElement)) return

  showDropDownMenu.value = true
  emit('menu-item-hovered', props.menuItem.name ?? '')
  if (hasColumns.value) {
    handleColumnFlyoutPositioning()
  }
}

const handleMouseLeave = () => {
  if (props.menuContext !== 'desktop') return

  showDropDownMenu.value = false
  emit('menu-item-hovered', '')
}

const handleArrowClick = () => {
  showDropDownMenu.value = !showDropDownMenu.value
  emit('menu-item-hovered', showDropDownMenu.value ? (props.menuItem.name ?? '') : '')
  showColumn.value = null

  if (hasColumns.value) {
    handleColumnFlyoutPositioning()
  }
}
</script>

<template>
  <div ref="rootEl"
       :key="menuItem.name"
       :class="{
         'bx-js-show-dropdown': showDropDownMenu
       }">
    <div :class="{
           'bx-js-has-dropdown': level === 1 && hasFlyoutContent
         }"
         @mouseover="handleMouseOver">
      <a :href="menuItem.url"
         :target="menuItem.target"
         :class="{
           [`bx-${menuType}__navi-item-with-image`]: menuItem.icon?.imageId && !menuItem.displayIconOnly
         }"
         :rel="rel">
        <img v-if="menuItem.icon?.imageId"
             :src="imageIconUrl"
             :title="menuItem.icon?.seoName"
             :alt="menuItem.icon?.imageAltText"
             :class="`bx-${menuType}__navi-item-image`">
        <template v-if="!menuItem.displayIconOnly">
          {{ menuItem.name }}
        </template>
      </a>
      <button v-if="level === 1 && (hasChildren || hasColumns) && menuContext !== 'footer'"
              :class="`bx-${menuType}__navi-item-arrow`"
              :aria-label="`Unter-Menü '${menuItem.name}' ${showDropDownMenu ? 'schließen' : 'öffnen'}`"
              aria-haspopup="true"
              :aria-expanded="showDropDownMenu"
              :aria-controls="flyoutId"
              @click="handleArrowClick">
        <Icon name="arrow_toggle" />
      </button>
    </div>
    <div v-if="hasFlyoutContent"
         :id="flyoutId"
         :class="{
           'bx-js-is-dropdown': true,
           [`bx-${menuType}__navi-list`]: true,
           [`bx-${menuType}__navi-list--level-${level}`]: true
         }"
         @mouseleave="handleMouseLeave">
      <div v-if="hasChildren"
           :class="`bx-${menuType}__menu-regular`">
        <div v-if="menuItem.label && menuContext !== 'burger'"
             :class="`bx-${menuType}__navi-list--label`">
          {{ menuItem.label }}
        </div>
        <MenuItem v-for="(item, index) in menuItem.children"
                  :key="index"
                  :menu-item="item"
                  :level="(level + 1)"
                  :menu-type="menuType"
                  :menu-context="menuContext"
                  :brand="brand"
                  :burger-only="burgerOnly" />
      </div>
      <template v-if="hasColumns">
        <div v-for="(column, colIndex) in resolvedColumns"
             :key="colIndex"
             :class="{
               [`bx-${menuType}__menu-column`]: true,
               [`bx-${menuType}__menu-column--show`]: showColumn === colIndex,
               [`bx-${menuType}__menu-column--extended`]: column.isExtendedMenuColumn
             }">
          <div v-if="column.title"
               :class="`bx-${menuType}__navi-list--label`"
               :aria-label="`Unter-Menü '${column.title}' ${showDropDownMenu ? 'schließen' : 'öffnen'}`"
               aria-haspopup="true"
               :aria-expanded="showDropDownMenu"
               :aria-controls="flyoutId"
               @click="showColumn = showColumn === colIndex ? null : colIndex">
            {{ column.title }}
            <button :class="`bx-${menuType}__navi-item-arrow`">
              <Icon name="arrow_toggle" />
            </button>
          </div>
          <MenuItem v-for="(child, childIndex) in column.children"
                    :key="`col-${colIndex}-child-${childIndex}`"
                    :menu-item="child"
                    :level="(level + 1)"
                    :menu-type="menuType"
                    :menu-context="menuContext"
                    :brand="brand"
                    :burger-only="burgerOnly" />
        </div>
      </template>
    </div>
  </div>
</template>
